import { supabase } from '@/integrations/supabase/client';
import { isStandaloneMode } from '@/utils/platform';
import { logger } from '@/lib/logger';

// First-party usage instrumentation. Writes to public.app_events (RLS: insert-own only).
//
// Deliberately narrow: no message content, no child names, no free text, no query strings.
// Anything that could carry a name or a symptom stays out of `metadata` — the whole point is
// to answer "who opened what, when", which needs none of that.

export const ANALYTICS_EVENTS = {
  APP_OPEN: 'app_open',
  PAGE_VIEW: 'page_view',
  CTA_CLICK: 'cta_click',
  // 21/07: telemetria de leitura do ReaderV3 (livro/capítulo/segundos ativos).
  // Motivo: reading_time_minutes do reader legado ficou 0 pra sempre (nunca
  // ligada) e o V3 era localStorage-only — "tá tendo consumo?" era inrespondível.
  READING_PROGRESS: 'reading_progress',
  // 30/08: tela de boas-vindas em vídeo pós-quiz (P1/POPPY). metadata.action:
  // shown | completed | skipped. O baseline que ela ataca: 57,4% das contas
  // novas completam o quiz e nunca fazem mais nada.
  ONBOARDING_VIDEO: 'onboarding_video',
  // 31/08: vídeo da POPPY no empty-state do chat (P2). metadata.action:
  // shown | played | completed. Alvo: 58% das contas novas nunca falam com a
  // Sarah — medir se quem assiste manda mais primeira mensagem.
  SARAH_INTRO_VIDEO: 'sarah_intro_video',
  // 16/09: quais parâmetros chegam na URL da página de cadastro pós-compra.
  // metadata.qs_keys = só os NOMES dos parâmetros, nunca os valores — a pergunta
  // é "a Hotmart manda o email do comprador?", e ela se responde com as chaves.
  // Gravado DEPOIS do cadastro, porque quem cai no /welcome-hp ainda é anônimo e
  // a RLS de insert-own descarta evento sem sessão.
  LANDING_PARAMS: 'landing_params',
  // 20/09: velocidade real medida no aparelho da usuária (LCP, CLS, INP, TTFB,
  // FCP) mais o tipo de conexão e o número de núcleos. Existe porque o app
  // nunca teve medição de performance nenhuma: o que havia era um número de
  // README sem data nem fonte. Uma linha por sessão, enviada quando a aba sai
  // de cena. Ver src/lib/webVitals.ts.
  WEB_VITALS: 'web_vitals',
  // Onda 3 "Your purchases" (21/09/2026): exposição do bloco, erro de consulta, e a
  // pergunta de valor. Sem texto: a resposta escrita vive em purchase_value_answers.
  PURCHASES_BLOCK_SHOWN: 'purchases_block_shown',
  PURCHASES_BLOCK_ERROR: 'purchases_block_error',
  PURCHASES_PAGE_SHOWN: 'purchases_page_shown',
  PURCHASE_PIECE_OPENED: 'purchase_piece_opened',
  PURCHASE_VALUE_PROMPT_SHOWN: 'purchase_value_prompt_shown',
  PURCHASE_VALUE_ANSWERED: 'purchase_value_answered',
  PURCHASE_VALUE_DISMISSED: 'purchase_value_dismissed',
  // 23/09/2026: o quiz passo a passo. metadata.step (role | name | goals) e
  // metadata.action (shown | next | back | saved | save_failed). Existe porque as
  // telas novas do quiz (blueprint voz-da-cuidadora) têm piso de 96% de conclusão
  // e, sem evento por passo, só daria para ver o total cair, nunca ONDE ela desiste.
  QUIZ_STEP: 'quiz_step',
  // 23/09/2026: /magic-words v3. Exposição real por seção (IntersectionObserver, aba
  // visível), uma vez por visita e seção; para o trio, cada frase por situação
  // (metadata.section = phrase-1|2|3, metadata.situation). Exposição não é leitura.
  // Existe porque até aqui ninguém sabia até onde ela rolava a página.
  MAGIC_WORDS_SEEN: 'magic_words_seen',
  // Resultado observável do "Save this card": share_handoff | download_requested |
  // cancelled_or_unavailable | error. Nenhum deles quer dizer "salvou em Fotos".
  MAGIC_WORDS_SAVE: 'magic_words_save',
  // 25/09/2026: Phrases refeita. Abrir uma frase nunca foi medido (o script_usage
  // é "I said this", e não gravou de 15/03 a 25/09). metadata: script_id,
  // origin (search | category | list | saved | recent | home | link),
  // version (v2 = acervo novo, v1 = antigo), pick (single | age | broad | choose | exact).
  // Abrir não é ler nem usar.
  PHRASE_OPEN: 'phrase_open',
} as const;

type EventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

const SESSION_KEY = 'analytics_session_id';
const OPEN_KEY = 'analytics_app_open_sent';

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled: still emit events, just ungrouped.
    return 'no-session-storage';
  }
}

// Route ids are opaque (uuids, slugs) and carry no PII, but query strings can — strip them.
function cleanPath(path: string): string {
  return path.split('?')[0].split('#')[0].slice(0, 200);
}

/** Returns true when the event actually reached the table. */
export async function trackEvent(
  eventName: EventName,
  options: { path?: string; metadata?: Record<string, string | number | boolean> } = {}
): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    // Anonymous visitors can't satisfy the insert-own RLS policy; skip rather than fail.
    if (!userId) return false;

    const { error } = await supabase.from('app_events').insert({
      user_id: userId,
      session_id: getSessionId(),
      event_name: eventName,
      path: options.path ? cleanPath(options.path) : null,
      is_standalone: isStandaloneMode(),
      metadata: options.metadata ?? {},
    });

    if (error) {
      logger.warn('[analytics] insert failed (non-fatal)', error);
      return false;
    }
    return true;
  } catch (err) {
    // Analytics must never break a user flow.
    logger.warn('[analytics] tracking failed (non-fatal)', err);
    return false;
  }
}

// Once per tab session, so a PWA left open for a week doesn't inflate opens. The flag is
// only set after a successful send: a user who lands logged-out and signs in still gets
// their open counted on the next route change.
export async function trackAppOpen(path: string): Promise<void> {
  try {
    if (sessionStorage.getItem(OPEN_KEY)) return;
  } catch {
    // Storage disabled — one open per page load is close enough.
  }
  const sent = await trackEvent(ANALYTICS_EVENTS.APP_OPEN, { path });
  if (!sent) return;
  try {
    sessionStorage.setItem(OPEN_KEY, '1');
  } catch {
    /* noop */
  }
}

export function trackPageView(path: string): void {
  void trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, { path });
}

export function trackCtaClick(cta: string, metadata: Record<string, string | number | boolean> = {}): void {
  void trackEvent(ANALYTICS_EVENTS.CTA_CLICK, { path: window.location.pathname, metadata: { cta, ...metadata } });
}
